import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  Plus, Edit2, Trash2, RefreshCw, Save, X, 
  Calendar, MapPin, Clock, Database, Tag, Building2, Users, Mail,
  Settings, Image, Upload, Palette, Layers, GripVertical, Eye, Timer,
  Handshake, ExternalLink, ChevronUp, ChevronDown, FileSpreadsheet, Download, BarChart3, Book
} from 'lucide-react';
import { AnalyticsDashboard } from '../components/admin/AnalyticsDashboard';
import { UserManual } from '../components/admin/UserManual';
import { UserManagement } from '../components/admin/UserManagement';
import { CollectionsManager } from '../components/admin/CollectionsManager';
import { NavigationManager } from '../components/admin/NavigationManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_EVENT = {
  title: '', description: '', genre: '', event_type: 'directo', venue: '',
  date: '', time: '', duration_minutes: 120, image_url: '', featured: false,
  has_smart_sale: false,  // Si el evento tiene venta SMART
  show_on_home: true,  // Si se muestra en la home
  smart_sale_end_date: '',  // Fecha/hora de cierre de venta SMART
  meta_pixel_id: '',  // Meta Pixel ID para tracking
  meta_pixel_enabled: false,  // Si el tracking está activo
  crew: { author: '', director: '', cast: [], scenography: '', lighting: '', costume_design: '', choreography: '', musical_direction: '', orchestra: '' },
  cinemas: [], gallery: [], trailer_url: '', pdf_url: '',
  collection_tags: []
};

const DEFAULT_SLIDE = { image_url: '', title: '', subtitle: '', description: '' };

const EVENT_TYPES = [{ value: 'directo', label: 'En Directo' }, { value: 'grabado', label: 'Grabado' }];

export const Admin = () => {
  const [events, setEvents] = useState([]);
  const [genres, setGenres] = useState([]);
  const [cities, setCities] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberStats, setMemberStats] = useState({ total: 0, active: 0, by_genre: {}, by_city: {} });
  const [newsletterStats, setNewsletterStats] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null);
  const [heroSlides, setHeroSlides] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authHeader, setAuthHeader] = useState('');
  
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_EVENT);
  const [saving, setSaving] = useState(false);
  const [castInput, setCastInput] = useState('');
  const [cinemaInput, setCinemaInput] = useState({ 
    cinema_name: '', 
    city: '', 
    comunidad_autonoma: '', 
    ticket_url: '', 
    schedule: '', 
    projection_date: '', 
    embed_code: '', 
    website_url: '', 
    has_smart: false,
    sessions: [{ date: '', time: '' }],  // Support for multiple sessions
    latitude: '',  // Coordenada de latitud para el mapa
    longitude: '',  // Coordenada de longitud para el mapa
    address: ''  // Dirección del cine
  });

  // Lista de Comunidades Autónomas de España
  const COMUNIDADES_AUTONOMAS = [
    'Andalucía', 'Aragón', 'Asturias', 'Baleares', 'Canarias', 
    'Cantabria', 'Castilla-La Mancha', 'Castilla y León', 'Cataluña',
    'Comunidad Valenciana', 'Extremadura', 'Galicia', 'La Rioja',
    'Madrid', 'Murcia', 'Navarra', 'País Vasco', 'Ceuta', 'Melilla'
  ];

  const [isSlideDialogOpen, setIsSlideDialogOpen] = useState(false);
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [slideForm, setSlideForm] = useState(DEFAULT_SLIDE);

  const [isGenreDialogOpen, setIsGenreDialogOpen] = useState(false);
  const [editingGenre, setEditingGenre] = useState(null);
  const [genreForm, setGenreForm] = useState({ value: '', label: '' });

  const [isCityDialogOpen, setIsCityDialogOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [cityForm, setCityForm] = useState({ value: '', label: '' });

  // Partners state
  const [partners, setPartners] = useState([]);
  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false);
  const [editingPartnerIndex, setEditingPartnerIndex] = useState(null);
  const [partnerForm, setPartnerForm] = useState({ name: '', logo_url: '', website_url: '' });

  const fileInputRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('adminAuth');
    if (stored) {
      setAuthHeader(stored);
      setIsAuthenticated(true);
      fetchAll(stored);
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    const header = `Basic ${auth}`;
    try {
      await axios.get(`${API}/events`, { headers: { Authorization: header } });
      localStorage.setItem('adminAuth', header);
      setAuthHeader(header);
      setIsAuthenticated(true);
      fetchAll(header);
      toast.success('Sesión iniciada');
    } catch (error) {
      toast.error('Credenciales incorrectas');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setAuthHeader('');
  };

  const fetchAll = async (auth) => {
    setLoading(true);
    try {
      const headers = { Authorization: auth || authHeader };
      const [eventsRes, genresRes, citiesRes, subsRes, statsRes, configRes, uploadsRes, membersRes] = await Promise.all([
        axios.get(`${API}/events`, { headers }),
        axios.get(`${API}/genres`),
        axios.get(`${API}/cities`),
        axios.get(`${API}/admin/newsletter/subscribers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/newsletter/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/site-config`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/uploads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/members`, { headers }).catch(() => ({ data: { members: [], stats: {} } }))
      ]);
      setEvents(eventsRes.data);
      setGenres(genresRes.data);
      setCities(citiesRes.data);
      setSubscribers(subsRes.data);
      setNewsletterStats(statsRes.data);
      setSiteConfig(configRes.data);
      setHeroSlides(configRes.data?.hero_slides || []);
      setPartners(configRes.data?.partners || []);
      setUploads(uploadsRes.data);
      setMembers(membersRes.data.members || []);
      setMemberStats(membersRes.data.stats || { total: 0, active: 0, by_genre: {}, by_city: {} });
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    try {
      await axios.post(`${API}/admin/seed`, {}, { headers: { Authorization: authHeader } });
      toast.success('Base de datos poblada');
      fetchAll();
    } catch (error) {
      toast.error('Error al poblar');
    }
  };

  // Event handlers
  const openCreateEventDialog = () => {
    setEditingEvent(null);
    setFormData({ ...DEFAULT_EVENT, genre: genres[0]?.value || '' });
    setCastInput('');
    setIsEventDialogOpen(true);
  };

  const openEditEventDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title, description: event.description, genre: event.genre, event_type: event.event_type,
      venue: event.venue, date: event.date, time: event.time, duration_minutes: event.duration_minutes,
      image_url: event.image_url, featured: event.featured,
      has_smart_sale: event.has_smart_sale || false,
      show_on_home: event.show_on_home !== false,  // Default to true if not set
      smart_sale_end_date: event.smart_sale_end_date || '',
      meta_pixel_id: event.meta_pixel_id || '',
      meta_pixel_enabled: event.meta_pixel_enabled || false,
      crew: event.crew || DEFAULT_EVENT.crew,
      cinemas: (event.cinemas || []).map(c => ({
        ...c,
        comunidad_autonoma: c.comunidad_autonoma || '',
        website_url: c.website_url || '',
        has_smart: c.has_smart || false,
        embed_code: c.embed_code || ''
      })),
      gallery: event.gallery || [],
      trailer_url: event.trailer_url || '',
      pdf_url: event.pdf_url || '',
      collection_tags: event.collection_tags || []
    });
    setCastInput((event.crew?.cast || []).join(', '));
    setIsEventDialogOpen(true);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        crew: {
          ...formData.crew,
          cast: castInput.split(',').map(s => s.trim()).filter(Boolean)
        },
        collection_tags: typeof formData.collection_tags === 'string' 
          ? formData.collection_tags.split(',').map(s => s.trim()).filter(Boolean) 
          : (formData.collection_tags || [])
      };
      
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, payload, { headers: { Authorization: authHeader } });
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API}/admin/events`, payload, { headers: { Authorization: authHeader } });
        toast.success('Evento creado');
      }
      setIsEventDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, { headers: { Authorization: authHeader } });
      toast.success('Evento eliminado');
      fetchAll();
    } catch (error) {
      toast.error('Error');
    }
  };

  // Cinema handlers for event
  const addCinema = () => {
    if (!cinemaInput.cinema_name || !cinemaInput.city) return;
    // Ensure sessions array is properly formatted
    const cinemaToAdd = {
      ...cinemaInput,
      sessions: cinemaInput.sessions.filter(s => s.date || s.time)
    };
    // If no sessions but has projection_date/schedule, create session from them
    if (cinemaToAdd.sessions.length === 0 && (cinemaInput.projection_date || cinemaInput.schedule)) {
      cinemaToAdd.sessions = [{ date: cinemaInput.projection_date, time: cinemaInput.schedule }];
    }
    setFormData(prev => ({
      ...prev,
      cinemas: [...prev.cinemas, cinemaToAdd]
    }));
    setCinemaInput({ 
      cinema_name: '', 
      city: '', 
      comunidad_autonoma: '', 
      ticket_url: '', 
      schedule: '', 
      projection_date: '', 
      embed_code: '', 
      website_url: '', 
      has_smart: false,
      sessions: [{ date: '', time: '' }],
      latitude: '',
      longitude: '',
      address: ''
    });
  };

  // Helper function to parse Excel dates
  const parseExcelDate = (value) => {
    if (!value) return '';
    
    // If it's already a string in format YYYY-MM-DD or DD/MM/YYYY
    if (typeof value === 'string') {
      // Check if it's DD/MM/YYYY format
      const ddmmyyyy = value.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (ddmmyyyy) {
        const [_, day, month, year] = ddmmyyyy;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      // Check if already YYYY-MM-DD
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return value;
      }
      return value;
    }
    
    // If it's an Excel serial number (number of days since 1899-12-30)
    if (typeof value === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return '';
  };

  // Helper function to parse Excel time
  const parseExcelTime = (value) => {
    if (!value) return '';
    
    // If it's already a string like "20:00" or "20:00:00"
    if (typeof value === 'string') {
      const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (timeMatch) {
        return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
      }
      return value;
    }
    
    // If it's an Excel time fraction (e.g., 0.833333 for 20:00)
    if (typeof value === 'number' && value < 1) {
      const totalMinutes = Math.round(value * 24 * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    
    // If it's a number greater than 1 (could be datetime serial)
    if (typeof value === 'number') {
      const timeFraction = value % 1;
      if (timeFraction > 0) {
        const totalMinutes = Math.round(timeFraction * 24 * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
    }
    
    return '';
  };

  // Import cinemas from Excel file
  const handleExcelImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary', cellDates: false, cellNF: true, cellText: true });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { raw: true });
        
        const importedCinemas = jsonData.map(row => {
          // Parse date - could be Excel serial or string
          const rawDate = row['Fecha'] || row['fecha'] || row['projection_date'] || '';
          const parsedDate = parseExcelDate(rawDate);
          
          // Parse time - could be Excel fraction or string
          const rawTime = row['Horario (opcional)'] || row['Horario'] || row['horario'] || row['schedule'] || '';
          const parsedTime = parseExcelTime(rawTime);
          
          // Support for multiple sessions (Fecha2, Horario2, etc. or semicolon-separated)
          const sessions = [];
          
          // Add primary session if date exists
          if (parsedDate || parsedTime) {
            sessions.push({ date: parsedDate, time: parsedTime });
          }
          
          // Check for additional sessions (Fecha2/Horario2, Fecha3/Horario3, etc.)
          for (let i = 2; i <= 10; i++) {
            const additionalDate = row[`Fecha${i}`] || row[`fecha${i}`] || '';
            const additionalTime = row[`Horario${i}`] || row[`horario${i}`] || '';
            if (additionalDate || additionalTime) {
              sessions.push({ 
                date: parseExcelDate(additionalDate), 
                time: parseExcelTime(additionalTime) 
              });
            }
          }
          
          return {
            cinema_name: row['Nombre'] || row['nombre'] || row['cinema_name'] || '',
            city: row['Ciudad'] || row['ciudad'] || row['city'] || '',
            comunidad_autonoma: row['Comunidad'] || row['comunidad'] || row['comunidad_autonoma'] || '',
            schedule: parsedTime,
            projection_date: parsedDate,
            sessions: sessions.length > 0 ? sessions : [{ date: parsedDate, time: parsedTime }],
            website_url: row['Web'] || row['web'] || row['website_url'] || '',
            ticket_url: row['URL Entradas'] || row['url_entradas'] || row['ticket_url'] || '',
            has_smart: row['SMART'] === 'Si' || row['SMART'] === 'SI' || row['SMART'] === 'Sí' || row['SMART'] === 'sí' || row['SMART'] === 'si' || row['has_smart'] === true || String(row['SMART']).toLowerCase() === 'true' || false,
            embed_code: row['Embed'] || row['embed_code'] || '',
            latitude: parseFloat(row['Latitud'] || row['latitud'] || row['latitude'] || '') || null,
            longitude: parseFloat(row['Longitud'] || row['longitud'] || row['longitude'] || '') || null,
            address: row['Direccion'] || row['direccion'] || row['address'] || row['Dirección'] || ''
          };
        }).filter(c => c.cinema_name && c.city);
        
        if (importedCinemas.length > 0) {
          setFormData(prev => ({
            ...prev,
            cinemas: [...prev.cinemas, ...importedCinemas]
          }));
          toast.success(`${importedCinemas.length} cines importados correctamente`);
        } else {
          toast.error('No se encontraron cines válidos en el archivo');
        }
      } catch (error) {
        toast.error('Error al leer el archivo Excel');
        console.error(error);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // Download Excel template
  const downloadExcelTemplate = () => {
    const isGrabado = formData.event_type === 'grabado';
    const template = [
      { 
        Nombre: 'Cine Ejemplo', 
        Ciudad: 'Madrid', 
        Comunidad: 'Madrid', 
        ...(isGrabado ? { 
          Fecha: '2026-03-15',
          Fecha2: '2026-03-16',
          Fecha3: ''
        } : {}),
        'Horario (opcional)': '20:00',
        ...(isGrabado ? { 
          Horario2: '18:00',
          Horario3: ''
        } : {}),
        Web: 'https://www.ejemplo.com', 
        'URL Entradas': '', 
        SMART: 'No', 
        Embed: '',
        Latitud: '40.4168',
        Longitud: '-3.7038',
        Direccion: 'Gran Vía 32, 28013 Madrid'
      },
      { 
        Nombre: 'Otro Cine', 
        Ciudad: 'Barcelona', 
        Comunidad: 'Cataluña', 
        ...(isGrabado ? { 
          Fecha: '2026-03-16',
          Fecha2: '2026-03-17',
          Fecha3: '2026-03-18'
        } : {}),
        'Horario (opcional)': '19:30',
        ...(isGrabado ? { 
          Horario2: '22:00',
          Horario3: '17:00'
        } : {}),
        Web: 'https://www.otrocine.com', 
        'URL Entradas': '', 
        SMART: 'Si', 
        Embed: '',
        Latitud: '41.3851',
        Longitud: '2.1734',
        Direccion: 'Av. Diagonal 3, 08019 Barcelona'
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // Nombre
      { wch: 15 }, // Ciudad
      { wch: 15 }, // Comunidad
      ...(isGrabado ? [
        { wch: 12 }, // Fecha
        { wch: 12 }, // Fecha2
        { wch: 12 }, // Fecha3
      ] : []),
      { wch: 10 }, // Horario
      ...(isGrabado ? [
        { wch: 10 }, // Horario2
        { wch: 10 }, // Horario3
      ] : []),
      { wch: 25 }, // Web
      { wch: 25 }, // URL Entradas
      { wch: 8 },  // SMART
      { wch: 30 }, // Embed
      { wch: 12 }, // Latitud
      { wch: 12 }, // Longitud
      { wch: 35 }, // Direccion
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cines');
    XLSX.writeFile(wb, 'plantilla_cines.xlsx');
  };

  // Edit existing cinema embed_code
  const updateCinemaEmbedCode = (index, embedCode) => {
    setFormData(prev => ({
      ...prev,
      cinemas: prev.cinemas.map((c, i) => i === index ? { ...c, embed_code: embedCode } : c)
    }));
  };

  const removeCinema = (index) => {
    setFormData(prev => ({
      ...prev,
      cinemas: prev.cinemas.filter((_, i) => i !== index)
    }));
  };

  // Hero Slides handlers
  const openCreateSlideDialog = () => { 
    setEditingSlideIndex(null); 
    setSlideForm(DEFAULT_SLIDE); 
    setIsSlideDialogOpen(true); 
  };
  
  const openEditSlideDialog = (index) => { 
    setEditingSlideIndex(index); 
    setSlideForm(heroSlides[index]); 
    setIsSlideDialogOpen(true); 
  };

  const handleSlideSubmit = async (e) => {
    e.preventDefault();
    let newSlides;
    if (editingSlideIndex !== null) {
      newSlides = [...heroSlides];
      newSlides[editingSlideIndex] = slideForm;
    } else {
      newSlides = [...heroSlides, slideForm];
    }
    try {
      await axios.put(`${API}/admin/site-config`, { hero_slides: newSlides }, { headers: { Authorization: authHeader } });
      setHeroSlides(newSlides);
      toast.success('Slide guardado');
      setIsSlideDialogOpen(false);
    } catch (error) {
      toast.error('Error al guardar slide');
    }
  };

  const handleDeleteSlide = async (index) => {
    if (!window.confirm('¿Eliminar este slide?')) return;
    const newSlides = heroSlides.filter((_, i) => i !== index);
    try {
      await axios.put(`${API}/admin/site-config`, { hero_slides: newSlides }, { headers: { Authorization: authHeader } });
      setHeroSlides(newSlides);
      toast.success('Slide eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const moveSlide = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= heroSlides.length) return;
    const newSlides = [...heroSlides];
    [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
    try {
      await axios.put(`${API}/admin/site-config`, { hero_slides: newSlides }, { headers: { Authorization: authHeader } });
      setHeroSlides(newSlides);
    } catch (error) {
      toast.error('Error al reordenar');
    }
  };

  // Partner handlers
  const openCreatePartnerDialog = () => { 
    setEditingPartnerIndex(null); 
    setPartnerForm({ name: '', logo_url: '', website_url: '' }); 
    setIsPartnerDialogOpen(true); 
  };

  const openEditPartnerDialog = (index) => { 
    setEditingPartnerIndex(index); 
    setPartnerForm(partners[index]); 
    setIsPartnerDialogOpen(true); 
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    let newPartners;
    if (editingPartnerIndex !== null) {
      newPartners = [...partners];
      newPartners[editingPartnerIndex] = partnerForm;
    } else {
      newPartners = [...partners, partnerForm];
    }
    try {
      await axios.put(`${API}/admin/site-config`, { partners: newPartners }, { headers: { Authorization: authHeader } });
      setPartners(newPartners);
      toast.success('Partner guardado');
      setIsPartnerDialogOpen(false);
    } catch (error) {
      toast.error('Error al guardar partner');
    }
  };

  const handleDeletePartner = async (index) => {
    if (!window.confirm('¿Eliminar este partner?')) return;
    const newPartners = partners.filter((_, i) => i !== index);
    try {
      await axios.put(`${API}/admin/site-config`, { partners: newPartners }, { headers: { Authorization: authHeader } });
      setPartners(newPartners);
      toast.success('Partner eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const movePartner = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= partners.length) return;
    const newPartners = [...partners];
    [newPartners[index], newPartners[newIndex]] = [newPartners[newIndex], newPartners[index]];
    try {
      await axios.put(`${API}/admin/site-config`, { partners: newPartners }, { headers: { Authorization: authHeader } });
      setPartners(newPartners);
    } catch (error) {
      toast.error('Error al reordenar');
    }
  };

  // Genre handlers
  const openCreateGenreDialog = () => { setEditingGenre(null); setGenreForm({ value: '', label: '' }); setIsGenreDialogOpen(true); };
  const openEditGenreDialog = (genre) => { setEditingGenre(genre); setGenreForm({ value: genre.value, label: genre.label }); setIsGenreDialogOpen(true); };
  
  const handleGenreSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGenre) {
        await axios.put(`${API}/admin/genres/${editingGenre.id}`, genreForm, { headers: { Authorization: authHeader } });
      } else {
        await axios.post(`${API}/admin/genres`, genreForm, { headers: { Authorization: authHeader } });
      }
      toast.success('Género guardado');
      setIsGenreDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteGenre = async (genreId) => {
    if (!window.confirm('¿Eliminar?')) return;
    try { await axios.delete(`${API}/admin/genres/${genreId}`, { headers: { Authorization: authHeader } }); toast.success('Eliminado'); fetchAll(); } catch { toast.error('Error'); }
  };

  // City handlers
  const openCreateCityDialog = () => { setEditingCity(null); setCityForm({ value: '', label: '' }); setIsCityDialogOpen(true); };
  const openEditCityDialog = (city) => { setEditingCity(city); setCityForm({ value: city.value, label: city.label }); setIsCityDialogOpen(true); };
  
  const handleCitySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCity) {
        await axios.put(`${API}/admin/cities/${editingCity.id}`, cityForm, { headers: { Authorization: authHeader } });
      } else {
        await axios.post(`${API}/admin/cities`, cityForm, { headers: { Authorization: authHeader } });
      }
      toast.success('Ciudad guardada');
      setIsCityDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteCity = async (cityId) => {
    if (!window.confirm('¿Eliminar?')) return;
    try { await axios.delete(`${API}/admin/cities/${cityId}`, { headers: { Authorization: authHeader } }); toast.success('Eliminado'); fetchAll(); } catch { toast.error('Error'); }
  };

  // Site config handlers
  const handleSiteConfigUpdate = async (field, value) => {
    try {
      await axios.put(`${API}/admin/site-config`, { [field]: value }, { headers: { Authorization: authHeader } });
      setSiteConfig(prev => ({ ...prev, [field]: value }));
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  // Upload handlers
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: authHeader, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Imagen subida');
      fetchAll();
    } catch (error) {
      toast.error('Error al subir imagen');
    }
  };

  const handleDeleteUpload = async (filename) => {
    if (!window.confirm('¿Eliminar imagen?')) return;
    try {
      await axios.delete(`${API}/admin/uploads/${filename}`, { headers: { Authorization: authHeader } });
      toast.success('Imagen eliminada');
      fetchAll();
    } catch (error) {
      toast.error('Error');
    }
  };

  const copyImageUrl = (url) => {
    navigator.clipboard.writeText(`${BACKEND_URL}${url}`);
    toast.success('URL copiada al portapapeles');
  };

  const updateFormData = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const updateCrewData = (field, value) => setFormData(prev => ({ ...prev, crew: { ...prev.crew, [field]: value } }));

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="admin-login">
        <div className="w-full max-w-md p-8 bg-[var(--surface)] border border-white/5">
          <h1 className="font-['Playfair_Display'] text-3xl text-white text-center mb-8">Panel de Administración</h1>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Usuario</label>
              <Input type="text" value={credentials.username} onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))} placeholder="Usuario" className="bg-transparent border-white/20 text-white rounded-none" data-testid="admin-username" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Contraseña</label>
              <Input type="password" value={credentials.password} onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))} placeholder="Contraseña" className="bg-transparent border-white/20 text-white rounded-none" data-testid="admin-password" />
            </div>
            <Button type="submit" className="w-full btn-primary" data-testid="admin-login-btn">Iniciar Sesión</Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="admin-page">
      {/* Header */}
      <div className="section-container py-8 border-b border-white/5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-2">Administración</span>
            <h1 className="font-['Playfair_Display'] text-4xl text-white">Panel de Control</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleSeedDatabase} className="btn-outline" data-testid="seed-db-btn"><Database size={16} className="mr-2" />Poblar DB</Button>
            <Button variant="outline" onClick={() => fetchAll()} className="btn-outline"><RefreshCw size={16} className="mr-2" />Actualizar</Button>
            <Button variant="ghost" onClick={handleLogout} className="text-white/50 hover:text-white">Salir</Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="section-container py-8">
        <Tabs defaultValue="events" className="w-full">
          <TabsList className="bg-[var(--surface)] border border-white/5 p-1 mb-8 flex-wrap">
            <TabsTrigger value="events" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Calendar size={16} className="mr-2" />Eventos</TabsTrigger>
            <TabsTrigger value="hero" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Layers size={16} className="mr-2" />Hero/Slides</TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Handshake size={16} className="mr-2" />Partners</TabsTrigger>
            <TabsTrigger value="genres" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Tag size={16} className="mr-2" />Géneros</TabsTrigger>
            <TabsTrigger value="cities" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Building2 size={16} className="mr-2" />Ciudades</TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Users size={16} className="mr-2" />myEventCinema</TabsTrigger>
            <TabsTrigger value="newsletter" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Mail size={16} className="mr-2" />Newsletter</TabsTrigger>
            <TabsTrigger value="config" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Settings size={16} className="mr-2" />Configuración</TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Image size={16} className="mr-2" />Galería</TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><BarChart3 size={16} className="mr-2" />Analíticas</TabsTrigger>
            <TabsTrigger value="collections" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Layers size={16} className="mr-2" />Colecciones</TabsTrigger>
            <TabsTrigger value="navigation" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><GripVertical size={16} className="mr-2" />Navegación</TabsTrigger>
            <TabsTrigger value="manual" className="data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black"><Book size={16} className="mr-2" />Manual</TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="flex justify-end mb-6">
              <Button onClick={openCreateEventDialog} className="btn-primary" data-testid="create-event-btn"><Plus size={16} className="mr-2" />Nuevo Evento</Button>
            </div>
            {loading ? <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 skeleton" />)}</div> : events.length === 0 ? (
              <div className="text-center py-20 bg-[var(--surface)] border border-white/5">
                <p className="text-white/50 mb-4">No hay eventos</p>
                <Button onClick={handleSeedDatabase} className="btn-primary"><Database size={16} className="mr-2" />Poblar con datos</Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead><tr><th>Evento</th><th>Género</th><th>Tipo</th><th>Ciudad</th><th>Cines</th><th>Fecha</th><th>★</th><th>Home</th><th>Pixel</th><th>Acciones</th></tr></thead>
                  <tbody>
                    {events.map(event => (
                      <tr key={event.id} className={event.show_on_home === false ? 'opacity-50' : ''}>
                        <td>
                          <div className="flex items-center gap-4">
                            <img src={event.image_url} alt={event.title} className="w-16 h-12 object-cover" />
                            <div><p className="text-white font-medium">{event.title}</p><p className="text-white/50 text-sm">{event.venue}</p></div>
                          </div>
                        </td>
                        <td><span className="text-white/70 capitalize">{event.genre}</span></td>
                        <td><span className={event.event_type === 'directo' ? 'badge-live' : 'badge-recorded'}>{event.event_type === 'directo' ? 'Directo' : 'Grabado'}</span></td>
                        <td className="text-white/70">{event.city}</td>
                        <td className="text-[var(--primary)]">{event.cinemas?.length || 0}</td>
                        <td><div className="text-white/70">{event.date}<br/><span className="text-sm text-white/50">{event.time}</span></div></td>
                        <td>{event.featured && <span className="text-[var(--primary)]">★</span>}</td>
                        <td>{event.show_on_home !== false ? <span className="text-green-500">✓</span> : <span className="text-white/30">✗</span>}</td>
                        <td>{event.meta_pixel_enabled && event.meta_pixel_id ? <span className="text-blue-500" title={`Pixel: ${event.meta_pixel_id}`}>📊</span> : <span className="text-white/30">-</span>}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditEventDialog(event)} className="text-white/50 hover:text-white"><Edit2 size={16} /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteEvent(event.id)} className="text-white/50 hover:text-red-500"><Trash2 size={16} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Hero/Slides Tab */}
          <TabsContent value="hero">
            <div className="flex justify-between items-center mb-6">
              <p className="text-white/50 text-sm">Configura las imágenes y textos del slider de la página principal</p>
              <Button onClick={openCreateSlideDialog} className="btn-primary"><Plus size={16} className="mr-2" />Nuevo Slide</Button>
            </div>
            
            {heroSlides.length === 0 ? (
              <div className="p-12 bg-[var(--surface)] border border-white/5 text-center">
                <Layers size={48} className="text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No hay slides configurados</p>
                <Button onClick={openCreateSlideDialog} className="btn-outline mt-4">Crear primer slide</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {heroSlides.map((slide, index) => (
                  <div key={index} className="bg-[var(--surface)] border border-white/5 p-4 flex gap-4 items-start">
                    <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="icon" onClick={() => moveSlide(index, -1)} disabled={index === 0} className="text-white/50 hover:text-white h-6 w-6"><GripVertical size={14} /></Button>
                      <span className="text-white/30 text-xs text-center">{index + 1}</span>
                      <Button variant="ghost" size="icon" onClick={() => moveSlide(index, 1)} disabled={index === heroSlides.length - 1} className="text-white/50 hover:text-white h-6 w-6"><GripVertical size={14} /></Button>
                    </div>
                    <div className="w-32 h-20 bg-black/50 overflow-hidden flex-shrink-0">
                      {slide.image_url && <img src={slide.image_url} alt={`Slide ${index + 1}`} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">{slide.title || 'Sin título'} <span className="text-[var(--primary)]">{slide.subtitle}</span></p>
                      <p className="text-white/50 text-sm truncate">{slide.description || 'Sin descripción'}</p>
                      <p className="text-white/30 text-xs truncate mt-1">{slide.image_url}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditSlideDialog(index)} className="text-white/50 hover:text-white"><Edit2 size={16} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteSlide(index)} className="text-white/50 hover:text-red-500"><Trash2 size={16} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl text-white font-semibold">Partners y Colaboradores</h3>
                <p className="text-white/50 text-sm">Logos que aparecen en el carrusel de la página de inicio</p>
              </div>
              <Button onClick={openCreatePartnerDialog} className="btn-primary"><Plus size={16} className="mr-2" />Nuevo Partner</Button>
            </div>
            
            {partners.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-white/20">
                <Handshake size={48} className="mx-auto text-white/20 mb-4" />
                <p className="text-white/50 mb-4">No hay partners configurados</p>
                <Button onClick={openCreatePartnerDialog} className="btn-outline">
                  <Plus size={16} className="mr-2" />Añadir primer partner
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {partners.map((partner, index) => (
                  <div key={index} className="p-4 bg-[var(--surface)] border border-white/10 hover:border-[var(--primary)]/30 transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-white/10 flex items-center justify-center overflow-hidden">
                          {partner.logo_url ? (
                            <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-contain" />
                          ) : (
                            <Handshake size={24} className="text-white/30" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{partner.name || <span className="text-white/40 italic">Sin nombre</span>}</p>
                          {partner.website_url && (
                            <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="text-[var(--primary)] text-sm flex items-center gap-1 hover:underline">
                              <ExternalLink size={12} />Web
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => movePartner(index, -1)} disabled={index === 0} className="text-white/50 hover:text-white h-8 w-8"><ChevronUp size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => movePartner(index, 1)} disabled={index === partners.length - 1} className="text-white/50 hover:text-white h-8 w-8"><ChevronDown size={14} /></Button>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditPartnerDialog(index)} className="text-white/50 hover:text-white h-8 w-8"><Edit2 size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeletePartner(index)} className="text-white/50 hover:text-red-500 h-8 w-8"><Trash2 size={14} /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Genres Tab */}
          <TabsContent value="genres">
            <div className="flex justify-end mb-6"><Button onClick={openCreateGenreDialog} className="btn-primary"><Plus size={16} className="mr-2" />Nuevo Género</Button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {genres.map(genre => (
                <div key={genre.id} className="p-4 bg-[var(--surface)] border border-white/5 flex items-center justify-between">
                  <div><p className="text-white font-medium">{genre.label}</p><p className="text-white/50 text-sm">{genre.value}</p></div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEditGenreDialog(genre)} className="text-white/50 hover:text-white"><Edit2 size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteGenre(genre.id)} className="text-white/50 hover:text-red-500"><Trash2 size={16} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Cities Tab */}
          <TabsContent value="cities">
            <div className="flex justify-end mb-6"><Button onClick={openCreateCityDialog} className="btn-primary"><Plus size={16} className="mr-2" />Nueva Ciudad</Button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cities.map(city => (
                <div key={city.id} className="p-4 bg-[var(--surface)] border border-white/5 flex items-center justify-between">
                  <div><p className="text-white font-medium">{city.label}</p><p className="text-white/50 text-sm">{city.value}</p></div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEditCityDialog(city)} className="text-white/50 hover:text-white"><Edit2 size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteCity(city.id)} className="text-white/50 hover:text-red-500"><Trash2 size={16} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* myEventCinema Members Tab */}
          <TabsContent value="members">
            <UserManagement 
              members={members} 
              memberStats={memberStats} 
              authHeader={authHeader} 
              onRefresh={fetchAll} 
            />
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter">
            {newsletterStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="p-6 bg-[var(--surface)] border border-white/5 text-center">
                  <p className="text-4xl font-['Playfair_Display'] text-[var(--primary)]">{newsletterStats.total_subscribers}</p>
                  <p className="text-white/50 text-sm uppercase tracking-widest mt-2">Total Suscriptores</p>
                </div>
                <div className="p-6 bg-[var(--surface)] border border-white/5 text-center">
                  <p className="text-4xl font-['Playfair_Display'] text-green-500">{newsletterStats.active_subscribers}</p>
                  <p className="text-white/50 text-sm uppercase tracking-widest mt-2">Activos</p>
                </div>
                <div className="p-6 bg-[var(--surface)] border border-white/5 text-center">
                  <p className="text-4xl font-['Playfair_Display'] text-white/50">{newsletterStats.total_subscribers - newsletterStats.active_subscribers}</p>
                  <p className="text-white/50 text-sm uppercase tracking-widest mt-2">Inactivos</p>
                </div>
              </div>
            )}

            {/* ActiveCampaign Integration Section */}
            <div className="mb-8 p-6 bg-gradient-to-r from-[var(--primary)]/10 to-transparent border border-[var(--primary)]/30">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg text-white font-medium flex items-center gap-2">
                    <Mail size={20} className="text-[var(--primary)]" />
                    ActiveCampaign Integrado
                  </h3>
                  <p className="text-white/50 text-sm mt-1">
                    Los nuevos suscriptores y socios se sincronizan automáticamente con ActiveCampaign
                  </p>
                </div>
                <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs uppercase tracking-wider">
                  Conectado
                </span>
              </div>
              
              <div className="flex flex-wrap gap-3 mt-4">
                <Button 
                  variant="outline" 
                  className="btn-outline"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/activecampaign/sync-all-subscribers`, {}, { headers: { Authorization: authHeader } });
                      toast.success('Sincronización de suscriptores iniciada');
                    } catch (error) {
                      toast.error('Error al sincronizar');
                    }
                  }}
                >
                  <RefreshCw size={16} className="mr-2" />
                  Sincronizar Suscriptores
                </Button>
                <Button 
                  variant="outline" 
                  className="btn-outline"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/activecampaign/sync-all-members`, {}, { headers: { Authorization: authHeader } });
                      toast.success('Sincronización de socios iniciada');
                    } catch (error) {
                      toast.error('Error al sincronizar');
                    }
                  }}
                >
                  <Users size={16} className="mr-2" />
                  Sincronizar Socios
                </Button>
              </div>
              
              <p className="text-white/30 text-xs mt-4">
                Los tags en ActiveCampaign se crean automáticamente según géneros y ciudades seleccionados.
                Configura automatizaciones en ActiveCampaign para enviar emails de bienvenida y notificaciones.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Email</th><th>Géneros</th><th>Ciudades</th><th>Fecha</th><th>Estado</th></tr></thead>
                <tbody>
                  {subscribers.map(sub => (
                    <tr key={sub.id}>
                      <td className="text-white">{sub.email}</td>
                      <td><div className="flex flex-wrap gap-1">{sub.genres.map(g => <span key={g} className="text-xs px-2 py-0.5 bg-white/10">{g}</span>)}</div></td>
                      <td><div className="flex flex-wrap gap-1">{sub.cities.map(c => <span key={c} className="text-xs px-2 py-0.5 bg-white/10">{c}</span>)}</div></td>
                      <td className="text-white/50 text-sm">{new Date(sub.subscribed_at).toLocaleDateString('es-ES')}</td>
                      <td>{sub.active ? <span className="text-green-500">Activo</span> : <span className="text-white/30">Inactivo</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscribers.length === 0 && <p className="text-center text-white/50 py-8">No hay suscriptores</p>}
            </div>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config">
            <div className="max-w-2xl space-y-8">
              <h2 className="font-['Playfair_Display'] text-2xl text-white mb-6 flex items-center gap-2"><Settings size={24} className="text-[var(--primary)]" />Configuración del Sitio</h2>
              
              {siteConfig && (
                <div className="space-y-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Nombre del Sitio</label>
                    <Input defaultValue={siteConfig.site_name} onBlur={(e) => handleSiteConfigUpdate('site_name', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                  
                  {/* Logos Section */}
                  <div className="pt-6 border-t border-white/10">
                    <h3 className="text-lg text-white mb-4 flex items-center gap-2"><Image size={18} className="text-[var(--primary)]" />Logotipos</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Main Logo */}
                      <div className="bg-white/5 p-4 border border-white/10">
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Logo Principal (Header)</label>
                        <Input 
                          defaultValue={siteConfig.logo_url} 
                          onBlur={(e) => handleSiteConfigUpdate('logo_url', e.target.value)} 
                          placeholder="https://... o /uploads/logo.png" 
                          className="bg-transparent border-white/20 text-white rounded-none" 
                        />
                        {siteConfig.logo_url && (
                          <div className="mt-3 p-2 bg-black/30">
                            <img src={siteConfig.logo_url.startsWith('/') ? `${BACKEND_URL}${siteConfig.logo_url}` : siteConfig.logo_url} alt="Logo principal" className="h-12 object-contain" />
                          </div>
                        )}
                        <p className="text-white/30 text-xs mt-2">Logo que aparece en la barra de navegación</p>
                      </div>
                      
                      {/* Footer Logo */}
                      <div className="bg-white/5 p-4 border border-white/10">
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Logo Footer (Versión Digital)</label>
                        <Input 
                          defaultValue={siteConfig.footer_logo_url || ''} 
                          onBlur={(e) => handleSiteConfigUpdate('footer_logo_url', e.target.value)} 
                          placeholder="https://... o /uploads/logo-footer.png" 
                          className="bg-transparent border-white/20 text-white rounded-none" 
                        />
                        {siteConfig.footer_logo_url && (
                          <div className="mt-3 p-2 bg-black/30">
                            <img src={siteConfig.footer_logo_url.startsWith('/') ? `${BACKEND_URL}${siteConfig.footer_logo_url}` : siteConfig.footer_logo_url} alt="Logo footer" className="h-12 object-contain" />
                          </div>
                        )}
                        <p className="text-white/30 text-xs mt-2">Logo que aparece en la parte inferior del footer</p>
                      </div>
                    </div>
                    
                    <p className="text-white/30 text-xs mt-4">Sube imágenes en la pestaña "Galería" y copia su URL aquí</p>
                  </div>

                  <div className="pt-6 border-t border-white/10">
                    <h3 className="text-lg text-white mb-4 flex items-center gap-2"><Palette size={18} className="text-[var(--primary)]" />Colores del Tema</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Color Primario (Dorado)</label>
                        <div className="flex gap-2">
                          <input type="color" value={siteConfig.primary_color} onChange={(e) => handleSiteConfigUpdate('primary_color', e.target.value)} className="w-12 h-10 border-0 cursor-pointer" />
                          <Input value={siteConfig.primary_color} onChange={(e) => handleSiteConfigUpdate('primary_color', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none flex-1" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Color Secundario (Burdeos)</label>
                        <div className="flex gap-2">
                          <input type="color" value={siteConfig.secondary_color} onChange={(e) => handleSiteConfigUpdate('secondary_color', e.target.value)} className="w-12 h-10 border-0 cursor-pointer" />
                          <Input value={siteConfig.secondary_color} onChange={(e) => handleSiteConfigUpdate('secondary_color', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none flex-1" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Color de Fondo</label>
                        <div className="flex gap-2">
                          <input type="color" value={siteConfig.background_color} onChange={(e) => handleSiteConfigUpdate('background_color', e.target.value)} className="w-12 h-10 border-0 cursor-pointer" />
                          <Input value={siteConfig.background_color} onChange={(e) => handleSiteConfigUpdate('background_color', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none flex-1" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Color de Superficie</label>
                        <div className="flex gap-2">
                          <input type="color" value={siteConfig.surface_color} onChange={(e) => handleSiteConfigUpdate('surface_color', e.target.value)} className="w-12 h-10 border-0 cursor-pointer" />
                          <Input value={siteConfig.surface_color} onChange={(e) => handleSiteConfigUpdate('surface_color', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none flex-1" />
                        </div>
                      </div>
                    </div>
                    <p className="text-white/30 text-xs mt-4">Nota: Los cambios de color requieren recargar la página para verse reflejados completamente.</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <div className="mb-6 flex justify-between items-center">
              <h2 className="font-['Playfair_Display'] text-2xl text-white flex items-center gap-2"><Image size={24} className="text-[var(--primary)]" />Galería de Imágenes</h2>
              <div>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                <Button onClick={() => fileInputRef.current?.click()} className="btn-primary"><Upload size={16} className="mr-2" />Subir Imagen</Button>
              </div>
            </div>
            
            {uploads.length === 0 ? (
              <div className="text-center py-20 bg-[var(--surface)] border border-white/5">
                <Image size={48} className="mx-auto text-white/20 mb-4" />
                <p className="text-white/50">No hay imágenes subidas</p>
                <p className="text-white/30 text-sm mt-2">Sube imágenes para usar como logos, fondos o en eventos</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {uploads.map(file => (
                  <div key={file.filename} className="group relative bg-[var(--surface)] border border-white/5 p-2">
                    <img src={`${BACKEND_URL}${file.url}`} alt={file.filename} className="w-full aspect-square object-cover" />
                    <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      <Button size="sm" onClick={() => copyImageUrl(file.url)} className="w-full text-xs bg-[var(--primary)] text-black">Copiar URL</Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteUpload(file.filename)} className="w-full text-xs border-red-500 text-red-500 hover:bg-red-500 hover:text-white">Eliminar</Button>
                    </div>
                    <p className="text-white/50 text-xs truncate mt-2">{file.filename}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          {/* Collections Tab */}
          <TabsContent value="collections">
            <CollectionsManager authHeader={authHeader} />
          </TabsContent>

          {/* Navigation Tab */}
          <TabsContent value="navigation">
            <NavigationManager authHeader={authHeader} />
          </TabsContent>

          {/* User Manual Tab */}
          <TabsContent value="manual">
            <UserManual />
          </TabsContent>
        </Tabs>
      </div>

      {/* Event Dialog - Extended */}
      <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
        <DialogContent className="bg-[var(--surface)] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Playfair_Display'] text-2xl">{editingEvent ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle></DialogHeader>
          <form onSubmit={handleEventSubmit} className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Título *</label>
                <Input value={formData.title} onChange={(e) => updateFormData('title', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" required />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Descripción *</label>
                <Textarea value={formData.description} onChange={(e) => updateFormData('description', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none min-h-[80px]" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Género</label>
                <Select value={formData.genre} onValueChange={(v) => updateFormData('genre', v)}>
                  <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-white/10">{genres.map(g => <SelectItem key={g.value} value={g.value} className="text-white hover:bg-white/10">{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Tipo</label>
                <Select value={formData.event_type} onValueChange={(v) => updateFormData('event_type', v)}>
                  <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-white/10">{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/10">{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Teatro / Recinto *</label>
                <Input value={formData.venue} onChange={(e) => updateFormData('venue', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" placeholder="Ej: Teatro Real de Madrid" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Fecha</label>
                <Input type="text" value={formData.date} onChange={(e) => updateFormData('date', e.target.value)} placeholder="YYYY-MM-DD" className="bg-transparent border-white/20 text-white rounded-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Hora {formData.event_type === 'directo' && <span className="text-white/30">(recomendado)</span>}</label>
                <Input type="text" value={formData.time} onChange={(e) => updateFormData('time', e.target.value)} placeholder="HH:MM" className="bg-transparent border-white/20 text-white rounded-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Duración (min)</label>
                <Input type="number" value={formData.duration_minutes} onChange={(e) => updateFormData('duration_minutes', parseInt(e.target.value))} className="bg-transparent border-white/20 text-white rounded-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">URL Imagen Principal</label>
                <Input type="url" value={formData.image_url} onChange={(e) => updateFormData('image_url', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">URL Tráiler (YouTube/Vimeo)</label>
                <Input type="url" value={formData.trailer_url || ''} onChange={(e) => updateFormData('trailer_url', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" placeholder="https://www.youtube.com/watch?v=..." />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">URL Ficha PDF (Enlace externo)</label>
                <Input type="url" value={formData.pdf_url || ''} onChange={(e) => updateFormData('pdf_url', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" placeholder="https://ejemplo.com/ficha.pdf" />
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.featured} onChange={(e) => updateFormData('featured', e.target.checked)} className="w-5 h-5 accent-[var(--primary)]" />
                  <span className="text-white/70">Evento destacado</span>
                </label>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.show_on_home !== false} onChange={(e) => updateFormData('show_on_home', e.target.checked)} className="w-5 h-5 accent-[var(--primary)]" />
                  <span className="text-white/70">Mostrar en Home</span>
                  <span className="text-white/40 text-xs">(al desmarcar, va a Eventos Pasados)</span>
                </label>
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Galería de imágenes (URLs separadas por coma)</label>
                <Textarea 
                  value={(formData.gallery || []).join(', ')} 
                  onChange={(e) => updateFormData('gallery', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} 
                  className="bg-transparent border-white/20 text-white rounded-none min-h-[60px]" 
                  placeholder="https://imagen1.jpg, https://imagen2.jpg, ..." 
                />
              </div>
            </div>

            {/* Crew / Ficha Artística */}
            <div className="border-t border-white/10 pt-6">
              <h3 className="text-lg text-white mb-4 flex items-center gap-2"><Users size={18} className="text-[var(--primary)]" />Ficha Artística</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Autor / Compositor</label>
                  <Input value={formData.crew?.author || ''} onChange={(e) => updateCrewData('author', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Director</label>
                  <Input value={formData.crew?.director || ''} onChange={(e) => updateCrewData('director', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Dirección Musical</label>
                  <Input value={formData.crew?.musical_direction || ''} onChange={(e) => updateCrewData('musical_direction', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Coreografía</label>
                  <Input value={formData.crew?.choreography || ''} onChange={(e) => updateCrewData('choreography', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Escenografía</label>
                  <Input value={formData.crew?.scenography || ''} onChange={(e) => updateCrewData('scenography', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Iluminación</label>
                  <Input value={formData.crew?.lighting || ''} onChange={(e) => updateCrewData('lighting', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Vestuario</label>
                  <Input value={formData.crew?.costume_design || ''} onChange={(e) => updateCrewData('costume_design', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Orquesta</label>
                  <Input value={formData.crew?.orchestra || ''} onChange={(e) => updateCrewData('orchestra', e.target.value)} className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Elenco / Intérpretes (separados por coma)</label>
                  <Input value={castInput} onChange={(e) => setCastInput(e.target.value)} placeholder="Nombre 1, Nombre 2, Nombre 3..." className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
              </div>
            </div>

            {/* SMART Sale Toggle for Event */}
            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[var(--primary)]/10 to-transparent border border-[var(--primary)]/30">
                <div className="flex items-center gap-3">
                  <Timer size={24} className="text-[var(--primary)]" />
                  <div>
                    <h3 className="text-white font-medium">Venta SMART</h3>
                    <p className="text-white/50 text-sm">Activa la venta anticipada con descuento del 25%</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={formData.has_smart_sale || false}
                    onChange={(e) => updateFormData('has_smart_sale', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white/50 after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[var(--primary)] peer-checked:after:bg-black"></div>
                  <span className="ms-3 text-sm font-medium text-white/70">{formData.has_smart_sale ? 'Activa' : 'Inactiva'}</span>
                </label>
              </div>
              
              {/* SMART Sale End Date - Only show when SMART is enabled */}
              {formData.has_smart_sale && (
                <div className="mt-4 p-4 bg-white/5 border border-white/10">
                  <label className="text-xs uppercase tracking-widest text-[var(--primary)] mb-2 block flex items-center gap-2">
                    <Clock size={14} />
                    Fecha y hora de cierre de venta SMART *
                  </label>
                  <Input 
                    type="text" 
                    value={formData.smart_sale_end_date || ''} 
                    onChange={(e) => updateFormData('smart_sale_end_date', e.target.value)} 
                    placeholder="YYYY-MM-DDTHH:MM"
                    className="bg-transparent border-white/20 text-white rounded-none" 
                    required={formData.has_smart_sale}
                  />
                  <p className="text-white/40 text-xs mt-2">
                    Cuando esta fecha/hora se alcance, el recuadro de venta SMART desaparecerá automáticamente de las tarjetas de eventos.
                  </p>
                </div>
              )}
            </div>

            {/* Meta Pixel Tracking */}
            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-500/10 to-transparent border border-blue-500/30">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"/>
                  </svg>
                  <div>
                    <h3 className="text-white font-medium">Meta Pixel (Facebook)</h3>
                    <p className="text-white/50 text-sm">Activa el tracking de Meta Pixel para este evento</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={formData.meta_pixel_enabled || false}
                    onChange={(e) => updateFormData('meta_pixel_enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white/50 after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-500 peer-checked:after:bg-white"></div>
                  <span className="ms-3 text-sm font-medium text-white/70">{formData.meta_pixel_enabled ? 'Activo' : 'Inactivo'}</span>
                </label>
              </div>
              
              {/* Meta Pixel ID - Always visible so user can set it up before enabling */}
              <div className="mt-4 p-4 bg-white/5 border border-white/10">
                <label className="text-xs uppercase tracking-widest text-blue-400 mb-2 block flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"/>
                  </svg>
                  Meta Pixel ID {formData.meta_pixel_enabled && '*'}
                </label>
                <Input 
                  type="text" 
                  value={formData.meta_pixel_id || ''} 
                  onChange={(e) => updateFormData('meta_pixel_id', e.target.value)} 
                  placeholder="Ej: 1234567890123456"
                  className="bg-transparent border-white/20 text-white rounded-none font-mono" 
                  required={formData.meta_pixel_enabled}
                />
                <p className="text-white/40 text-xs mt-2">
                  El Pixel ID se encuentra en tu{' '}
                  <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                    Meta Events Manager
                  </a>
                  . Solo se activará si el usuario acepta las cookies de marketing.
                </p>
                {formData.meta_pixel_enabled && !formData.meta_pixel_id && (
                  <p className="text-amber-400 text-xs mt-2 flex items-center gap-1">
                    ⚠️ El pixel está activado pero falta el Pixel ID
                  </p>
                )}
              </div>
            </div>

            {/* Collection Tags */}
            <div className="border-t border-white/10 pt-6">
              <div className="p-4 bg-white/5 border border-white/10">
                <label className="text-xs uppercase tracking-widest text-[var(--primary)] mb-2 block flex items-center gap-2">
                  <Tag size={14} />
                  Etiquetas de Colección
                </label>
                <Input
                  type="text"
                  value={Array.isArray(formData.collection_tags) ? formData.collection_tags.join(', ') : (formData.collection_tags || '')}
                  onChange={(e) => updateFormData('collection_tags', e.target.value)}
                  placeholder="ohpera-summer, festival-2026"
                  className="bg-transparent border-white/20 text-white rounded-none font-mono text-sm"
                />
                <p className="text-white/40 text-xs mt-2">
                  Etiquetas separadas por coma. Los eventos con estas etiquetas aparecerán en sus colecciones correspondientes.
                </p>
              </div>
            </div>

            {/* Cinemas */}
            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg text-white flex items-center gap-2"><Building2 size={18} className="text-[var(--primary)]" />Cines que proyectan este evento</h3>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={downloadExcelTemplate} className="text-white/50 hover:text-white text-xs">
                    <Download size={14} className="mr-1" />Plantilla
                  </Button>
                  <label className="cursor-pointer">
                    <input type="file" accept=".xlsx,.xls" onChange={handleExcelImport} className="hidden" />
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--primary)]/20 border border-[var(--primary)]/40 text-[var(--primary)] text-xs hover:bg-[var(--primary)]/30 transition-colors">
                      <FileSpreadsheet size={14} />Importar Excel
                    </span>
                  </label>
                  {formData.cinemas && formData.cinemas.length > 0 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      onClick={() => {
                        if (window.confirm(`¿Estás seguro de que quieres borrar los ${formData.cinemas.length} cines?`)) {
                          setFormData(prev => ({ ...prev, cinemas: [] }));
                          toast.success('Todos los cines han sido eliminados');
                        }
                      }} 
                      className="text-red-500/70 hover:text-red-500 hover:bg-red-500/10 text-xs"
                    >
                      <Trash2 size={14} className="mr-1" />Borrar todos
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                <Input value={cinemaInput.cinema_name} onChange={(e) => setCinemaInput(p => ({ ...p, cinema_name: e.target.value }))} placeholder="Nombre del cine" className="bg-transparent border-white/20 text-white rounded-none" />
                <Input value={cinemaInput.city} onChange={(e) => setCinemaInput(p => ({ ...p, city: e.target.value }))} placeholder="Ciudad" className="bg-transparent border-white/20 text-white rounded-none" />
                <Select value={cinemaInput.comunidad_autonoma} onValueChange={(value) => setCinemaInput(p => ({ ...p, comunidad_autonoma: value }))}>
                  <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none">
                    <SelectValue placeholder="Comunidad Autónoma" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-white/20">
                    {COMUNIDADES_AUTONOMAS.map(ca => (
                      <SelectItem key={ca} value={ca} className="text-white hover:bg-white/10">{ca}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Sessions/Schedules for the cinema */}
              {formData.event_type === 'grabado' && (
                <div className="mb-4 p-3 bg-white/5 border border-white/10">
                  <label className="text-xs uppercase tracking-widest text-[var(--primary)] mb-2 block">Sesiones (Fecha + Horario)</label>
                  <div className="space-y-2">
                    {(cinemaInput.sessions || [{ date: '', time: '' }]).map((session, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input 
                          type="text" 
                          value={session.date || ''} 
                          onChange={(e) => {
                            const newSessions = [...(cinemaInput.sessions || [{ date: '', time: '' }])];
                            newSessions[idx] = { ...newSessions[idx], date: e.target.value };
                            setCinemaInput(p => ({ ...p, sessions: newSessions, projection_date: newSessions[0]?.date || '' }));
                          }} 
                          placeholder="YYYY-MM-DD"
                          className="bg-transparent border-white/20 text-white rounded-none flex-1" 
                        />
                        <Input 
                          type="text"
                          value={session.time || ''} 
                          onChange={(e) => {
                            const newSessions = [...(cinemaInput.sessions || [{ date: '', time: '' }])];
                            newSessions[idx] = { ...newSessions[idx], time: e.target.value };
                            setCinemaInput(p => ({ ...p, sessions: newSessions, schedule: newSessions[0]?.time || '' }));
                          }} 
                          placeholder="HH:MM"
                          className="bg-transparent border-white/20 text-white rounded-none w-28" 
                        />
                        {idx > 0 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon"
                            onClick={() => {
                              const newSessions = cinemaInput.sessions.filter((_, i) => i !== idx);
                              setCinemaInput(p => ({ ...p, sessions: newSessions }));
                            }}
                            className="text-red-500 hover:text-red-400 h-8 w-8"
                          >
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost"
                    onClick={() => setCinemaInput(p => ({ ...p, sessions: [...(p.sessions || []), { date: '', time: '' }] }))}
                    className="mt-2 text-[var(--primary)] text-xs"
                  >
                    <Plus size={12} className="mr-1" /> Añadir otra sesión
                  </Button>
                </div>
              )}
              
              {/* Non-grabado: simple schedule input */}
              {formData.event_type !== 'grabado' && (
                <div className="mb-4">
                  <Input value={cinemaInput.schedule} onChange={(e) => setCinemaInput(p => ({ ...p, schedule: e.target.value }))} placeholder="Horario (ej: 20:00)" className="bg-transparent border-white/20 text-white rounded-none" />
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <Input value={cinemaInput.website_url} onChange={(e) => setCinemaInput(p => ({ ...p, website_url: e.target.value }))} placeholder="Web del cine" className="bg-transparent border-white/20 text-white rounded-none" />
                <Input value={cinemaInput.ticket_url} onChange={(e) => setCinemaInput(p => ({ ...p, ticket_url: e.target.value }))} placeholder="URL entradas" className="bg-transparent border-white/20 text-white rounded-none" />
                <Input value={cinemaInput.address || ''} onChange={(e) => setCinemaInput(p => ({ ...p, address: e.target.value }))} placeholder="Dirección" className="bg-transparent border-white/20 text-white rounded-none col-span-2" />
              </div>
              
              {/* Coordinates for map */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <Input 
                  type="number" 
                  step="any" 
                  value={cinemaInput.latitude || ''} 
                  onChange={(e) => setCinemaInput(p => ({ ...p, latitude: e.target.value }))} 
                  placeholder="Latitud (ej: 40.4168)" 
                  className="bg-transparent border-white/20 text-white rounded-none" 
                />
                <Input 
                  type="number" 
                  step="any" 
                  value={cinemaInput.longitude || ''} 
                  onChange={(e) => setCinemaInput(p => ({ ...p, longitude: e.target.value }))} 
                  placeholder="Longitud (ej: -3.7038)" 
                  className="bg-transparent border-white/20 text-white rounded-none" 
                />
                <Button type="button" onClick={addCinema} className="btn-outline px-4 col-span-2"><Plus size={16} className="mr-1" /> Añadir Cine</Button>
              </div>
              
              {formData.event_type === 'grabado' && (
                <p className="text-white/40 text-xs mb-4 -mt-2">
                  ℹ️ Para eventos grabados, cada cine puede tener su propia fecha y hora de proyección
                </p>
              )}
              
              {formData.cinemas.length > 0 && (
                <div className="space-y-4">
                  {formData.cinemas.map((cinema, index) => (
                    <div key={index} className={`p-4 border space-y-3 ${cinema.has_smart ? 'bg-[var(--primary)]/5 border-[var(--primary)]/30' : 'bg-white/5 border-white/10'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-white font-medium">{cinema.cinema_name}</span>
                          <span className="text-white/50 text-sm">({cinema.city})</span>
                          {cinema.comunidad_autonoma && <span className="text-[var(--primary)]/70 text-sm bg-[var(--primary)]/10 px-2 py-0.5">{cinema.comunidad_autonoma}</span>}
                          {/* Show sessions summary */}
                          {cinema.sessions && cinema.sessions.length > 0 ? (
                            <span className="text-white/50 text-sm">
                              {cinema.sessions.filter(s => s.date || s.time).map((s, i) => (
                                <span key={i} className="inline-flex items-center gap-1">
                                  {i > 0 && <span className="mx-1 text-white/30">|</span>}
                                  {s.date && <span>{s.date}</span>}
                                  {s.time && <span className="text-[var(--primary)]">{s.time}</span>}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <>
                              {cinema.projection_date && <span className="text-white/50 text-sm">{cinema.projection_date}</span>}
                              {cinema.schedule && <span className="text-[var(--primary)] text-sm">{cinema.schedule}</span>}
                            </>
                          )}
                          {cinema.has_smart && (
                            <span className="px-2 py-0.5 bg-[var(--primary)] text-black text-xs font-bold">SMART</span>
                          )}
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeCinema(index)} className="text-white/50 hover:text-red-500"><X size={16} /></Button>
                      </div>
                      
                      {/* Comunidad Autónoma Selector */}
                      <div>
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                          📍 Comunidad Autónoma
                        </label>
                        <Select 
                          value={cinema.comunidad_autonoma || ''} 
                          onValueChange={(value) => setFormData(prev => ({
                            ...prev,
                            cinemas: prev.cinemas.map((c, i) => i === index ? { ...c, comunidad_autonoma: value } : c)
                          }))}
                        >
                          <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none w-full md:w-64">
                            <SelectValue placeholder="Seleccionar Comunidad" />
                          </SelectTrigger>
                          <SelectContent className="bg-[var(--surface)] border-white/20">
                            {COMUNIDADES_AUTONOMAS.map(ca => (
                              <SelectItem key={ca} value={ca} className="text-white hover:bg-white/10">{ca}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Projection Date and Schedule - Show sessions for grabado events */}
                      {formData.event_type === 'grabado' && (
                        <div>
                          <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                            📅 Sesiones (Fecha + Horario)
                          </label>
                          <div className="space-y-2">
                            {(cinema.sessions && cinema.sessions.length > 0 ? cinema.sessions : [{ date: cinema.projection_date || '', time: cinema.schedule || '' }]).map((session, sessionIdx) => (
                              <div key={sessionIdx} className="flex gap-2 items-center">
                                <Input 
                                  type="text"
                                  value={session.date || ''} 
                                  onChange={(e) => {
                                    const newSessions = [...(cinema.sessions || [{ date: '', time: '' }])];
                                    newSessions[sessionIdx] = { ...newSessions[sessionIdx], date: e.target.value };
                                    setFormData(prev => ({
                                      ...prev,
                                      cinemas: prev.cinemas.map((c, i) => i === index ? { 
                                        ...c, 
                                        sessions: newSessions,
                                        projection_date: newSessions[0]?.date || ''
                                      } : c)
                                    }));
                                  }} 
                                  placeholder="YYYY-MM-DD"
                                  className="bg-transparent border-white/20 text-white rounded-none flex-1"
                                />
                                <Input 
                                  type="text"
                                  value={session.time || ''} 
                                  onChange={(e) => {
                                    const newSessions = [...(cinema.sessions || [{ date: '', time: '' }])];
                                    newSessions[sessionIdx] = { ...newSessions[sessionIdx], time: e.target.value };
                                    setFormData(prev => ({
                                      ...prev,
                                      cinemas: prev.cinemas.map((c, i) => i === index ? { 
                                        ...c, 
                                        sessions: newSessions,
                                        schedule: newSessions[0]?.time || ''
                                      } : c)
                                    }));
                                  }} 
                                  placeholder="HH:MM"
                                  className="bg-transparent border-white/20 text-white rounded-none w-28"
                                />
                                {sessionIdx > 0 && (
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => {
                                      const newSessions = (cinema.sessions || []).filter((_, i) => i !== sessionIdx);
                                      setFormData(prev => ({
                                        ...prev,
                                        cinemas: prev.cinemas.map((c, i) => i === index ? { ...c, sessions: newSessions } : c)
                                      }));
                                    }}
                                    className="text-red-500 hover:text-red-400 h-8 w-8"
                                  >
                                    <X size={14} />
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button 
                              type="button" 
                              variant="ghost"
                              onClick={() => {
                                const newSessions = [...(cinema.sessions || [{ date: '', time: '' }]), { date: '', time: '' }];
                                setFormData(prev => ({
                                  ...prev,
                                  cinemas: prev.cinemas.map((c, i) => i === index ? { ...c, sessions: newSessions } : c)
                                }));
                              }}
                              className="text-[var(--primary)] text-xs"
                            >
                              <Plus size={12} className="mr-1" /> Añadir sesión
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Website URL Field */}
                      <div>
                        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                          🌐 Web del Cine (botón "Comprar entrada")
                        </label>
                        <Input 
                          value={cinema.website_url || ''} 
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            cinemas: prev.cinemas.map((c, i) => i === index ? { ...c, website_url: e.target.value } : c)
                          }))} 
                          placeholder="https://www.cinesejemplo.com"
                          className="bg-transparent border-white/20 text-white rounded-none"
                        />
                      </div>

                      {/* SMART Toggle for this Cinema - Only show if event has SMART */}
                      {formData.has_smart_sale && (
                        <div className="p-3 bg-black/20 border border-white/10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Timer size={16} className="text-[var(--primary)]" />
                              <span className="text-white text-sm">Venta SMART para este cine</span>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={cinema.has_smart || false}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  cinemas: prev.cinemas.map((c, i) => i === index ? { ...c, has_smart: e.target.checked } : c)
                                }))}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white/50 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--primary)] peer-checked:after:bg-black"></div>
                            </label>
                          </div>
                          
                          {/* Embed Code Field - Only show if SMART is enabled for this cinema */}
                          {cinema.has_smart && (
                            <div className="mt-3">
                              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block flex items-center gap-2">
                                <code className="text-[var(--primary)]">&lt;iframe&gt;</code> Código Eventim (SMART)
                              </label>
                              <Textarea 
                                value={cinema.embed_code || ''} 
                                onChange={(e) => updateCinemaEmbedCode(index, e.target.value)} 
                                placeholder='<div style="padding: 0;"><iframe id="eventim-light-iframe..." src="https://www.eventim-light.com/..." width="100%" ...></iframe><script ...></script></div>'
                                className="bg-transparent border-white/20 text-white rounded-none min-h-[80px] font-mono text-xs"
                              />
                              {cinema.embed_code && (
                                <p className="text-green-400/70 text-xs mt-1">✓ iFrame SMART configurado</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-white/30 text-xs mt-4">
                {formData.has_smart_sale 
                  ? 'Activa la venta SMART individualmente para cada cine. Los cines con SMART mostrarán el botón "Oferta SMART -25%" además de "Comprar entrada".'
                  : 'Activa la venta SMART del evento para poder configurar qué cines tienen descuento.'}
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button type="button" variant="ghost" onClick={() => setIsEventDialogOpen(false)} className="text-white/50">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving}>
                {saving ? <><RefreshCw size={16} className="mr-2 animate-spin" />Guardando...</> : <><Save size={16} className="mr-2" />{editingEvent ? 'Actualizar' : 'Crear'}</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Genre Dialog */}
      <Dialog open={isGenreDialogOpen} onOpenChange={setIsGenreDialogOpen}>
        <DialogContent className="bg-[var(--surface)] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-['Playfair_Display'] text-2xl">{editingGenre ? 'Editar Género' : 'Nuevo Género'}</DialogTitle></DialogHeader>
          <form onSubmit={handleGenreSubmit} className="space-y-4 mt-4">
            <div><label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Valor (ID)</label><Input value={genreForm.value} onChange={(e) => setGenreForm(p => ({ ...p, value: e.target.value }))} className="bg-transparent border-white/20 text-white rounded-none" disabled={!!editingGenre} required /></div>
            <div><label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Etiqueta</label><Input value={genreForm.label} onChange={(e) => setGenreForm(p => ({ ...p, label: e.target.value }))} className="bg-transparent border-white/20 text-white rounded-none" required /></div>
            <div className="flex justify-end gap-3 pt-4"><Button type="button" variant="ghost" onClick={() => setIsGenreDialogOpen(false)} className="text-white/50">Cancelar</Button><Button type="submit" className="btn-primary"><Save size={16} className="mr-2" />Guardar</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* City Dialog */}
      <Dialog open={isCityDialogOpen} onOpenChange={setIsCityDialogOpen}>
        <DialogContent className="bg-[var(--surface)] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-['Playfair_Display'] text-2xl">{editingCity ? 'Editar Ciudad' : 'Nueva Ciudad'}</DialogTitle></DialogHeader>
          <form onSubmit={handleCitySubmit} className="space-y-4 mt-4">
            <div><label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Nombre</label><Input value={cityForm.value} onChange={(e) => setCityForm(p => ({ ...p, value: e.target.value, label: e.target.value }))} className="bg-transparent border-white/20 text-white rounded-none" required /></div>
            <div className="flex justify-end gap-3 pt-4"><Button type="button" variant="ghost" onClick={() => setIsCityDialogOpen(false)} className="text-white/50">Cancelar</Button><Button type="submit" className="btn-primary"><Save size={16} className="mr-2" />Guardar</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hero Slide Dialog */}
      <Dialog open={isSlideDialogOpen} onOpenChange={setIsSlideDialogOpen}>
        <DialogContent className="bg-[var(--surface)] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-['Playfair_Display'] text-2xl">{editingSlideIndex !== null ? 'Editar Slide' : 'Nuevo Slide'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSlideSubmit} className="space-y-4 mt-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">URL de la imagen *</label>
              <Input 
                value={slideForm.image_url} 
                onChange={(e) => setSlideForm(p => ({ ...p, image_url: e.target.value }))} 
                placeholder="https://..." 
                className="bg-transparent border-white/20 text-white rounded-none" 
                required 
              />
              {slideForm.image_url && (
                <div className="mt-2 aspect-video max-h-40 bg-black/50 overflow-hidden">
                  <img src={slideForm.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Título (primera línea)</label>
                <Input 
                  value={slideForm.title} 
                  onChange={(e) => setSlideForm(p => ({ ...p, title: e.target.value }))} 
                  placeholder="El Arte" 
                  className="bg-transparent border-white/20 text-white rounded-none" 
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Subtítulo (segunda línea, dorado)</label>
                <Input 
                  value={slideForm.subtitle} 
                  onChange={(e) => setSlideForm(p => ({ ...p, subtitle: e.target.value }))} 
                  placeholder="en Pantalla" 
                  className="bg-transparent border-white/20 text-white rounded-none" 
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Descripción</label>
              <Textarea 
                value={slideForm.description} 
                onChange={(e) => setSlideForm(p => ({ ...p, description: e.target.value }))} 
                placeholder="Texto descriptivo que aparece debajo del título..." 
                className="bg-transparent border-white/20 text-white rounded-none min-h-[80px]" 
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button type="button" variant="ghost" onClick={() => setIsSlideDialogOpen(false)} className="text-white/50">Cancelar</Button>
              <Button type="submit" className="btn-primary"><Save size={16} className="mr-2" />Guardar Slide</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Partner Dialog */}
      <Dialog open={isPartnerDialogOpen} onOpenChange={setIsPartnerDialogOpen}>
        <DialogContent className="bg-[var(--surface)] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-['Playfair_Display'] text-2xl">{editingPartnerIndex !== null ? 'Editar Partner' : 'Nuevo Partner'}</DialogTitle></DialogHeader>
          <form onSubmit={handlePartnerSubmit} className="space-y-4 mt-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Nombre del Partner (opcional)</label>
              <Input 
                value={partnerForm.name} 
                onChange={(e) => setPartnerForm(p => ({ ...p, name: e.target.value }))} 
                placeholder="Ej: Cinesa, Royal Opera House..." 
                className="bg-transparent border-white/20 text-white rounded-none" 
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">URL del Logo *</label>
              <Input 
                value={partnerForm.logo_url} 
                onChange={(e) => setPartnerForm(p => ({ ...p, logo_url: e.target.value }))} 
                placeholder="https://..." 
                className="bg-transparent border-white/20 text-white rounded-none" 
                required 
              />
              {partnerForm.logo_url && (
                <div className="mt-2 p-4 bg-white/5 flex items-center justify-center">
                  <img src={partnerForm.logo_url} alt="Preview" className="max-h-16 object-contain" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
              <p className="text-white/30 text-xs mt-1">Sube el logo a la Galería primero y copia la URL aquí</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Web del Partner (opcional)</label>
              <Input 
                value={partnerForm.website_url} 
                onChange={(e) => setPartnerForm(p => ({ ...p, website_url: e.target.value }))} 
                placeholder="https://www.ejemplo.com" 
                className="bg-transparent border-white/20 text-white rounded-none" 
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button type="button" variant="ghost" onClick={() => setIsPartnerDialogOpen(false)} className="text-white/50">Cancelar</Button>
              <Button type="submit" className="btn-primary"><Save size={16} className="mr-2" />Guardar Partner</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
